/**
 * Lecteur de rejeu — logique PURE (aucun DOM, aucun temps réel : l'écran appelle `avancer` avec le temps écoulé).
 *
 *  • `interpoler(points, t)` : où est le véhicule à l'instant t (interpolation linéaire entre deux positions, cap, vitesse, contact) ;
 *  • une absence de position de plus de `coupureS` n'est PAS parcourue en glissant : le marqueur reste au dernier point connu, puis saute ;
 *  • `avancer` : temps de l'histoire = temps réel écoulé × vitesse (×1, ×2, ×5, ×10, ×30), les arrêts longs pouvant être accélérés ;
 *  • `instantLePlusProche` : un clic sur le tracé devient un instant (projection du clic sur la ligne brisée) ;
 *  • `morceauxColores` : la ligne, découpée en morceaux de même classe de vitesse (pour la dessiner colorée sans un objet par segment).
 */
import { capEntre, distanceM, melangerCap } from './geo'
import type { Position } from './types'

export const VITESSES_REJEU = [1, 2, 5, 10, 30] as const
export type VitesseRejeu = (typeof VITESSES_REJEU)[number]

/** Facteur supplémentaire appliqué quand le véhicule est à l'arrêt depuis longtemps (option « accélérer les arrêts »). */
export const FACTEUR_ARRET = 20

export interface PointRejeu {
  t: number
  lat: number
  lng: number
  /** Cap en degrés ; `null` si jamais connu. */
  cap: number | null
  v: number | null
  ign: boolean | null
  /** Indice du point qui précède l'instant. */
  index: number
  /** L'instant tombe dans une absence de signal : le marqueur est resté au dernier point connu. */
  horsSignal: boolean
  /** L'instant tombe dans un arrêt long (les deux points encadrants sont proches mais éloignés dans le temps). */
  enArret: boolean
}

const PAUSE_ARRET_S = 120
const PAUSE_DISTANCE_M = 100

/** Plus grand indice i tel que points[i].t <= t (−1 si t précède tout). */
export function indexAvant(points: readonly Pick<Position, 't'>[], t: number): number {
  let lo = 0
  let hi = points.length - 1
  let r = -1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (points[mid]!.t <= t) {
      r = mid
      lo = mid + 1
    } else hi = mid - 1
  }
  return r
}

export function interpoler(points: readonly Position[], t: number, coupureS = 1800): PointRejeu | null {
  if (points.length === 0) return null
  const i = indexAvant(points, t)
  const a = points[Math.max(i, 0)]!
  if (i < 0 || i >= points.length - 1) {
    const x = i < 0 ? points[0]! : points[points.length - 1]!
    return { t: x.t, lat: x.lat, lng: x.lng, cap: x.cap, v: x.v, ign: x.ign, index: Math.max(i, 0), horsSignal: false, enArret: false }
  }
  const b = points[i + 1]!
  const dt = b.t - a.t
  const dist = distanceM(a.lat, a.lng, b.lat, b.lng)
  if (dt > coupureS) {
    // Longue absence de position : on ne fait pas glisser le marqueur à travers un trou. Deux points PROCHES = un véhicule garé dont le
    // boîtier se tait (contact coupé) : c'est un arrêt, pas une perte de signal.
    const gare = dist <= PAUSE_DISTANCE_M
    return { t, lat: a.lat, lng: a.lng, cap: a.cap, v: gare ? 0 : null, ign: a.ign, index: i, horsSignal: !gare, enArret: gare }
  }
  const u = dt > 0 ? (t - a.t) / dt : 0
  const cap =
    a.cap !== null && b.cap !== null
      ? melangerCap(a.cap, b.cap, u)
      : dist > 5
        ? capEntre(a.lat, a.lng, b.lat, b.lng)
        : (a.cap ?? b.cap)
  return {
    t,
    lat: a.lat + (b.lat - a.lat) * u,
    lng: a.lng + (b.lng - a.lng) * u,
    cap,
    v: a.v !== null && b.v !== null ? a.v + (b.v - a.v) * u : (a.v ?? b.v),
    ign: a.ign,
    index: i,
    horsSignal: false,
    enArret: dt > PAUSE_ARRET_S && dist < PAUSE_DISTANCE_M,
  }
}

export interface BornesRejeu {
  debut: number
  fin: number
}

export function bornesDe(points: readonly Pick<Position, 't'>[]): BornesRejeu | null {
  return points.length === 0 ? null : { debut: points[0]!.t, fin: points[points.length - 1]!.t }
}

/**
 * Fait avancer l'horloge du rejeu. `dtReelMs` = temps réel écoulé depuis le dernier appel. Rend le nouvel instant (borné) et si la
 * fin est atteinte. `accelererArrets` : à l'arrêt long, l'histoire passe `FACTEUR_ARRET` fois plus vite (sinon un arrêt de 2 h dure 2 h
 * en ×1) — on ne le fait que si l'appelant le demande, et le facteur est annoncé à l'écran.
 */
export function avancer(
  t: number,
  dtReelMs: number,
  vitesse: VitesseRejeu,
  bornes: BornesRejeu,
  options: { accelererArrets?: boolean; enArret?: boolean } = {},
): { t: number; fini: boolean } {
  const facteur = vitesse * (options.accelererArrets && options.enArret ? FACTEUR_ARRET : 1)
  const suivant = Math.min(bornes.fin, Math.max(bornes.debut, t + (Math.max(0, dtReelMs) / 1000) * facteur))
  return { t: suivant, fini: suivant >= bornes.fin }
}

/** Durée RÉELLE (secondes) d'un rejeu complet à cette vitesse, sans accélération des arrêts. */
export function dureeReelleRejeu(bornes: BornesRejeu, vitesse: VitesseRejeu): number {
  return Math.max(0, bornes.fin - bornes.debut) / vitesse
}

/**
 * Projette un clic (lat, lng) sur la ligne brisée et rend l'instant correspondant, ou `null` si le clic est trop loin du tracé
 * (`toleranceM`). Les trous de signal ne sont pas des segments cliquables.
 */
export function instantLePlusProche(
  points: readonly Position[],
  lat: number,
  lng: number,
  toleranceM = 200,
  coupureS = 1800,
): { t: number; distanceM: number } | null {
  if (points.length === 0) return null
  const kx = Math.cos((lat * Math.PI) / 180) * 111320
  const ky = 110540
  let meilleur: { t: number; distanceM: number } | null = null
  const consider = (d: number, t: number) => {
    if (!meilleur || d < meilleur.distanceM) meilleur = { t, distanceM: d }
  }
  if (points.length === 1) {
    consider(distanceM(lat, lng, points[0]!.lat, points[0]!.lng), points[0]!.t)
  }
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!
    const b = points[i + 1]!
    if (b.t - a.t > coupureS) continue
    const ax = (a.lng - lng) * kx, ay = (a.lat - lat) * ky
    const bx = (b.lng - lng) * kx, by = (b.lat - lat) * ky
    const dx = bx - ax, dy = by - ay
    const l2 = dx * dx + dy * dy
    const u = l2 === 0 ? 0 : Math.max(0, Math.min(1, (-ax * dx - ay * dy) / l2))
    consider(Math.hypot(ax + u * dx, ay + u * dy), a.t + (b.t - a.t) * u)
  }
  const m = meilleur as { t: number; distanceM: number } | null
  return m && m.distanceM <= toleranceM ? m : null
}

/* ───────────────────────── Couleur selon la vitesse ───────────────────────── */

export interface PalierVitesse {
  /** Vitesse minimale (km/h) du palier. */
  min: number
  libelle: string
  couleur: string
}

/** De l'arrêt au dépassement : sobre, lisible sur fond clair comme sombre, jamais le seul porteur de l'information (la légende dit les seuils). */
export const PALIERS_VITESSE: readonly PalierVitesse[] = [
  { min: 0, libelle: 'À l\'arrêt (moins de 5 km/h)', couleur: '#6B7A75' },
  { min: 5, libelle: '5 à 30 km/h', couleur: '#1B9E77' },
  { min: 30, libelle: '30 à 60 km/h', couleur: '#D8A31A' },
  { min: 60, libelle: '60 à 90 km/h', couleur: '#E0701B' },
  { min: 90, libelle: 'Plus de 90 km/h', couleur: '#C0392B' },
]

export function classeVitesse(kmh: number | null): number {
  if (kmh === null || !Number.isFinite(kmh)) return 0
  let c = 0
  for (let i = 0; i < PALIERS_VITESSE.length; i++) if (kmh >= PALIERS_VITESSE[i]!.min) c = i
  return c
}

export interface Morceau {
  classe: number
  /** `true` : ce morceau relie deux positions séparées par une absence de signal (à tracer en pointillé, sans vitesse). */
  coupure: boolean
  points: [number, number][]
}

/**
 * Découpe la trace en morceaux de même classe de vitesse. La vitesse d'un segment = celle rapportée à son point d'arrivée, sinon la
 * vitesse déduite de la distance. Deux morceaux voisins partagent leur point de jonction (la ligne reste continue).
 */
export function morceauxColores(points: readonly Position[], coupureS = 1800): Morceau[] {
  const sortie: Morceau[] = []
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!
    const b = points[i]!
    const dt = b.t - a.t
    const coupure = dt > coupureS && distanceM(a.lat, a.lng, b.lat, b.lng) > 100
    const v = b.v ?? (dt > 0 ? (distanceM(a.lat, a.lng, b.lat, b.lng) / dt) * 3.6 : null)
    const classe = coupure ? 0 : classeVitesse(v)
    const dernier = sortie[sortie.length - 1]
    if (dernier && dernier.classe === classe && dernier.coupure === coupure) dernier.points.push([b.lat, b.lng])
    else sortie.push({ classe, coupure, points: [[a.lat, a.lng], [b.lat, b.lng]] })
  }
  return sortie
}

/** Flèches de sens : un point tous les `pasM` mètres le long de la trace, avec le cap local — pour dessiner des chevrons. */
export function fleches(points: readonly Position[], pasM = 600): { lat: number; lng: number; cap: number }[] {
  const sortie: { lat: number; lng: number; cap: number }[] = []
  let cumul = 0
  let prochain = pasM / 2
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!
    const b = points[i]!
    const d = distanceM(a.lat, a.lng, b.lat, b.lng)
    if (d === 0) continue
    while (cumul + d >= prochain) {
      const u = (prochain - cumul) / d
      sortie.push({ lat: a.lat + (b.lat - a.lat) * u, lng: a.lng + (b.lng - a.lng) * u, cap: capEntre(a.lat, a.lng, b.lat, b.lng) })
      prochain += pasM
    }
    cumul += d
  }
  return sortie
}
